import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Briefcase, DollarSign, TrendingUp } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const STAGES = ['lead', 'proposal', 'negotiation', 'contract', 'active', 'completed', 'lost'];
const COLORS = { lead: 'bg-gray-100', proposal: 'bg-blue-100', negotiation: 'bg-yellow-100', contract: 'bg-purple-100', active: 'bg-green-100', completed: 'bg-emerald-100', lost: 'bg-red-100' };

export default function Deals() {
  const [deals, setDeals] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ title: '', organization_id: '', study_type: 'clinical_trial', value: 0, stage: 'lead', probability: 50, description: '', assigned_to: '' });

  useEffect(() => {
    Promise.all([api.get('/deals'), api.get('/organizations'), api.get('/users')]).then(([d, o, u]) => {
      setDeals(d.data); setOrganizations(o.data); setUsers(u.data);
    }).finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/deals', { ...formData, value: parseFloat(formData.value) });
      toast.success('Deal created');
      setDialogOpen(false);
      const res = await api.get('/deals');
      setDeals(res.data);
    } catch (error) {
      toast.error('Failed to create deal');
    }
  };

  const dealsByStage = STAGES.map(stage => ({ stage, deals: deals.filter(d => d.stage === stage) }));

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="space-y-6" data-testid="deals-page">
      <div className="flex justify-between items-center">
        <div><h1 className="text-4xl font-bold text-gray-900">Deals Pipeline</h1><p className="text-gray-600">Manage your deal pipeline</p></div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button className="bg-blue-600" data-testid="add-deal-button"><Plus className="w-4 h-4 mr-2" />Add Deal</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>New Deal</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><Label>Title *</Label><Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required data-testid="deal-title" /></div>
              <div><Label>Organization *</Label>
                <Select value={formData.organization_id} onValueChange={(v) => setFormData({...formData, organization_id: v})}>
                  <SelectTrigger data-testid="deal-org-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{organizations.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Study Type *</Label>
                  <Select value={formData.study_type} onValueChange={(v) => setFormData({...formData, study_type: v})}>
                    <SelectTrigger data-testid="deal-study-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clinical_trial">Clinical Trial</SelectItem>
                      <SelectItem value="preclinical">Preclinical</SelectItem>
                      <SelectItem value="analytical">Analytical</SelectItem>
                      <SelectItem value="regulatory">Regulatory</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Value ($) *</Label><Input type="number" value={formData.value} onChange={(e) => setFormData({...formData, value: e.target.value})} required data-testid="deal-value" /></div>
              </div>
              <div><Label>Assigned To *</Label>
                <Select value={formData.assigned_to} onValueChange={(v) => setFormData({...formData, assigned_to: v})}>
                  <SelectTrigger data-testid="deal-assigned-to"><SelectValue /></SelectTrigger>
                  <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-blue-600" data-testid="deal-submit">Create Deal</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex overflow-x-auto gap-4 pb-4">
        {dealsByStage.map(({ stage, deals: stageDeals }) => (
          <Card key={stage} className="min-w-[300px] flex-shrink-0 pipeline-stage" data-testid={`stage-${stage}`}>
            <CardHeader><CardTitle className="capitalize text-sm">{stage} ({stageDeals.length})</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {stageDeals.map(deal => {
                const org = organizations.find(o => o.id === deal.organization_id);
                return (
                  <Card key={deal.id} className={`${COLORS[stage]} border-none`} data-testid={`deal-card-${deal.id}`}>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-sm mb-2">{deal.title}</h3>
                      <p className="text-xs text-gray-600 mb-2">{org?.name}</p>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-green-700">${deal.value.toLocaleString()}</span>
                        <span className="text-gray-600">{deal.probability}%</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="stat-card"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Total Value</p><p className="text-3xl font-bold text-gray-900">${deals.reduce((sum, d) => sum + d.value, 0).toLocaleString()}</p></div><DollarSign className="w-12 h-12 text-green-500" /></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Active Deals</p><p className="text-3xl font-bold text-gray-900">{deals.filter(d => ['proposal', 'negotiation', 'contract', 'active'].includes(d.stage)).length}</p></div><Briefcase className="w-12 h-12 text-blue-500" /></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Win Rate</p><p className="text-3xl font-bold text-gray-900">{deals.length > 0 ? Math.round((deals.filter(d => d.stage === 'completed').length / deals.length) * 100) : 0}%</p></div><TrendingUp className="w-12 h-12 text-purple-500" /></div></CardContent></Card>
      </div>
    </div>
  );
}
